import React, { useState, useEffect } from 'react';

interface ClassroomInsightsPreviewProps {
  onClick?: () => void;
}

const ClassroomInsightsPreview: React.FC<ClassroomInsightsPreviewProps> = ({ onClick }) => {
  const [activeStudent, setActiveStudent] = useState(0);
  const [animatedBars, setAnimatedBars] = useState(false);

  // Animate bars on mount
  useEffect(() => {
    const timer = setTimeout(() => setAnimatedBars(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Cycle through students
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStudent((prev) => (prev + 1) % 6);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const students = [
    { name: 'Emma S.', mastery: 92, trend: 'up', status: 'excellent' },
    { name: 'Liam T.', mastery: 78, trend: 'up', status: 'good' },
    { name: 'Olivia M.', mastery: 85, trend: 'stable', status: 'good' },
    { name: 'Noah R.', mastery: 45, trend: 'down', status: 'alert' },
    { name: 'Ava K.', mastery: 67, trend: 'up', status: 'improving' },
    { name: 'James W.', mastery: 58, trend: 'stable', status: 'needs-help' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return '#10b981';
      case 'good': return '#3b82f6';
      case 'improving': return '#0ea5e9';
      case 'needs-help': return '#f59e0b';
      case 'alert': return '#ef4444';
      default: return '#64748b';
    }
  };

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return '↑';
    if (trend === 'down') return '↓';
    return '→';
  };

  const subjectBreakdown = [
    { subject: 'Maths', value: 76, color: '#3b82f6' },
    { subject: 'English', value: 82, color: '#10b981' },
    { subject: 'Science', value: 68, color: '#8b5cf6' },
  ];

  return (
    <div
      className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        width: '340px',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-2 border-b flex items-center justify-between"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          background: 'rgba(59, 130, 246, 0.05)',
        }}
      >
        <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
          Class Insights - Year 4
        </span>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#10b981' }} />
          <span className="text-[10px]" style={{ color: '#10b981' }}>
            LIVE
          </span>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-2 p-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
          <div className="text-lg font-bold" style={{ color: '#10b981' }}>28</div>
          <div className="text-[9px]" style={{ color: '#64748b' }}>Students</div>
        </div>
        <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
          <div className="text-lg font-bold" style={{ color: '#3b82f6' }}>76%</div>
          <div className="text-[9px]" style={{ color: '#64748b' }}>Avg Mastery</div>
        </div>
        <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
          <div className="text-lg font-bold" style={{ color: '#ef4444' }}>3</div>
          <div className="text-[9px]" style={{ color: '#64748b' }}>Alerts</div>
        </div>
      </div>

      {/* Student List */}
      <div className="p-3">
        <div className="text-[10px] font-semibold mb-2" style={{ color: '#64748b' }}>
          STUDENT MASTERY
        </div>
        <div className="space-y-2">
          {students.map((student, index) => (
            <div
              key={student.name}
              className="flex items-center gap-2 p-2 rounded-lg transition-all duration-300"
              style={{
                background: activeStudent === index ? 'rgba(59, 130, 246, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                border: activeStudent === index ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent',
              }}
            >
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                style={{ background: getStatusColor(student.status), color: 'white' }}
              >
                {student.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate" style={{ color: '#e2e8f0' }}>
                  {student.name}
                </div>
                <div className="h-1.5 rounded-full overflow-hidden mt-1" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: animatedBars ? `${student.mastery}%` : '0%',
                      background: getStatusColor(student.status),
                    }}
                  />
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold" style={{ color: getStatusColor(student.status) }}>
                  {student.mastery}%
                </div>
                <div
                  className="text-[10px]"
                  style={{ color: student.trend === 'up' ? '#10b981' : student.trend === 'down' ? '#ef4444' : '#64748b' }}
                >
                  {getTrendIcon(student.trend)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Subject Breakdown */}
      <div className="px-3 pb-3">
        <div className="text-[10px] font-semibold mb-2" style={{ color: '#64748b' }}>
          CLASS BY SUBJECT
        </div>
        <div className="flex gap-2">
          {subjectBreakdown.map((subject) => (
            <div
              key={subject.subject}
              className="flex-1 p-2 rounded-lg text-center"
              style={{ background: `${subject.color}15` }}
            >
              <div className="text-sm font-bold" style={{ color: subject.color }}>
                {subject.value}%
              </div>
              <div className="text-[9px]" style={{ color: '#94a3b8' }}>
                {subject.subject}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div
        className="px-4 py-2 flex items-center justify-between"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-center gap-2">
          <svg className="w-3 h-3" style={{ color: '#f59e0b' }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-[10px]" style={{ color: '#f59e0b' }}>
            3 students need attention
          </span>
        </div>
        <span className="text-[10px]" style={{ color: '#64748b' }}>
          Updated now
        </span>
      </div>

      {/* Expand overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(139, 92, 246, 0.9) 100%)',
            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4)',
          }}
        >
          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default ClassroomInsightsPreview;
